/**
 * ManagePublishedDialog
 * =====================
 *
 * Small action sheet shown when the user clicks the monetization
 * affordance ($) on a SignalCard / CompactSignalCard / row whose
 * underlying signal is already published to the marketplace.
 *
 * Surfaces the two things you almost always want from a published
 * listing in-context:
 *
 *   - **Edit listing** — opens the regular ``PublishListingDialog``
 *     in edit mode (signal_key + existing fields prefilled).
 *   - **Delist** — calls ``deleteListing`` (BE marks the row
 *     ``status='delisted'``). Disabled with a tooltip when the
 *     listing has active subscribers, since the BE refuses the
 *     delete and we want a one-glance reason instead of a 409.
 *
 * Keeps the parent SignalCard / signal_SignalList flow simple: the
 * dialog dispatches its own redux actions for delist + opens the
 * publish dialog via a callback.
 */

import React, { useState } from 'react';
import {
  Alert, Box, Chip, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Stack, Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';

import {
  CMButton, CMButtonOutlined, CMButtonError,
} from '../../containers/styledComponents';
import { deleteListing } from '../../redux/actions/marketplace/listings';

const ManagePublishedDialog = ({
  open, listing, onClose, onEdit, mutations, deleteListing,
}) => {
  const navigate = useNavigate();
  // Two-step delist when the listing still has active subscribers:
  // first click surfaces a cascade-confirm panel inside the dialog
  // explaining the impact; second click sends ``force=true`` and the
  // BE cancels every active sub before flipping to ``delisted``.
  const [confirmingCascade, setConfirmingCascade] = useState(false);
  if (!listing) return null;

  const subs = Number(listing.subscribers_count || 0);
  const hasActive = subs > 0;
  const busyDelist = mutations?.isLoading && mutations?.action === 'delete';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <StorefrontIcon fontSize="small" color="primary" />
        Manage published signal
      </DialogTitle>
      <DialogContent>
        <DialogContentText component="div">
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              {listing.title || listing.signal_key}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {listing.signal_key}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap"
                 sx={{ mb: 1.5 }}>
            <Chip
              size="small"
              label={listing.status || 'published'}
              color={listing.status === 'published' ? 'primary' : 'default'}
              variant="outlined"
            />
            <Chip
              size="small"
              label={`${subs} subscriber${subs === 1 ? '' : 's'}`}
              color={hasActive ? 'success' : 'default'}
              variant="outlined"
            />
            {listing.featured && (
              <Chip size="small" label="Featured" color="warning"
                    variant="outlined" />
            )}
          </Stack>
          {hasActive && !confirmingCascade && (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              {subs} subscriber{subs === 1 ? '' : 's'} {' '}
              currently rel{subs === 1 ? 'ies' : 'y'} on this signal.
              You can still delist, but doing so will{' '}
              <strong>cancel every active subscription</strong> and stop
              their billing starting today. Click <strong>Delist</strong>{' '}
              to confirm the impact.
            </Alert>
          )}
          {hasActive && confirmingCascade && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              Are you sure? This will <strong>immediately cancel{' '}
              {subs} subscription{subs === 1 ? '' : 's'}</strong> and the
              data will disappear from buyer workspaces on their next
              refresh. This <strong>cannot be undone</strong>.
            </Alert>
          )}
          {mutations?.error && mutations?.action === 'delete' && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {String(mutations.error)}
            </Alert>
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
        <CMButtonOutlined
          startIcon={<OpenInNewIcon />}
          onClick={() => {
            onClose && onClose();
            navigate(`/marketplace/listings/${listing.listing_id}`);
          }}
        >
          View on marketplace
        </CMButtonOutlined>
        <Box sx={{ flex: 1 }} />
        <CMButtonError
          startIcon={<BlockIcon />}
          disabled={busyDelist}
          onClick={() => {
            if (!hasActive) {
              deleteListing(listing.listing_id, false);
              return;
            }
            if (!confirmingCascade) {
              setConfirmingCascade(true);
              return;
            }
            // Second click on the cascade button: send force=true.
            deleteListing(listing.listing_id, true);
            setConfirmingCascade(false);
          }}
        >
          {busyDelist
            ? 'Delisting…'
            : (hasActive && confirmingCascade)
              ? 'Confirm cascade delist'
              : 'Delist'}
        </CMButtonError>
        <CMButton
          startIcon={<EditIcon />}
          onClick={() => {
            onEdit && onEdit(listing);
            onClose && onClose();
          }}
        >
          Edit listing
        </CMButton>
      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = (state) => ({
  mutations: state.marketplace_mutations,
});

const mapDispatchToProps = (dispatch) => ({
  deleteListing: (id, force = false) => dispatch(deleteListing(id, force)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ManagePublishedDialog);
